import {
	Box,
	Collapse,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Grid,
	Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { FormattedMessage, type IntlShape, useIntl } from "react-intl";
import {
	type FC,
	type ReactNode,
	createContext,
	Fragment as ReactFragment,
	useCallback,
	useContext,
	useLayoutEffect,
	useMemo,
	useState,
} from "react";
import ReactDiffViewer from "react-diff-viewer-continued";

import type { ConfigurationAuditEntry } from "@/schemas/audit/configuration-audit";

export type AuditRecord = ConfigurationAuditEntry & {
	defaultExpanded?: boolean;
};

export type DataTransformFunction = (data: unknown) => unknown;
export interface DiffViewActionColumn {
	readonly name: ReactNode;
	readonly maxWidth?: string;
	readonly cell: (...args: unknown[]) => ReactNode;
}

interface DiffViewColumn {
	readonly id: string;
	readonly name: ReactNode;
	readonly getValue: (row: AuditRecord, rowIndex: number) => ReactNode;
	readonly omit?: boolean;
	readonly width?: string;
	readonly maxWidth?: string;
	readonly align?: "left" | "center" | "right" | "justify" | "inherit";
}

const getAuditTypeDisplayName = (auditType: string, intl: IntlShape): string => {
	switch (auditType) {
		case "CVPRange":
			return intl.formatMessage({ id: "CVP Range", defaultMessage: "CVP Range" });
		case "LantechCaseSealer":
			return intl.formatMessage({ id: "LantechCaseSealer", defaultMessage: "ClosePro X" });
		case "PL1":
			return intl.formatMessage({ id: "PL1", defaultMessage: "LidPro X" });
		default:
			return auditType;
	}
};

const sanitizeRecord = (record: AuditRecord, additionalDataTransform?: DataTransformFunction): string => {
	const parsedData = JSON.parse(record.data) as Record<string, unknown>;
	const { TenantId: _TenantId, MessageId: _MessageId, Id: _Id, ...sanitizedData } = parsedData;

	const finalData = additionalDataTransform
		? (additionalDataTransform(sanitizedData) as Record<string, unknown>)
		: sanitizedData;

	return JSON.stringify(finalData, null, 2);
};

interface ExpandedRowContextValue {
	tableData: AuditRecord[];
	additionalDataTransform?: DataTransformFunction;
	compareAll: boolean;
}

const ExpandedRowContext = createContext<ExpandedRowContextValue>({
	tableData: [],
	compareAll: false,
});

const ExpandedRowContent: FC<{ readonly data: AuditRecord }> = ({ data: expandedRow }) => {
	const intl = useIntl();
	const { tableData, additionalDataTransform, compareAll } = useContext(ExpandedRowContext);
	const index = tableData.indexOf(expandedRow);
	if (index < 0) {
		return null;
	}

	const newValue = sanitizeRecord(tableData[index], additionalDataTransform);
	let oldValue = "{ }";

	if (!compareAll) {
		if (tableData.length > index + 1) {
			oldValue = sanitizeRecord(tableData[index + 1], additionalDataTransform);
		}
	} else {
		if (tableData.length > index + 1) {
			const auditType = tableData[index].auditType;
			const entityId = tableData[index].entityId;
			const nextIndex = tableData.findIndex(
				(i, ind) => ind > index && i.auditType === auditType && i.entityId === entityId,
			);
			if (nextIndex > 0) {
				oldValue = sanitizeRecord(tableData[nextIndex], additionalDataTransform);
			}
		}
	}

	return (
		<ReactDiffViewer
			newValue={newValue}
			oldValue={oldValue}
			splitView
			leftTitle={intl.formatMessage({ id: "Previous", defaultMessage: "Previous" })}
			rightTitle={intl.formatMessage({ id: "Changes", defaultMessage: "Changes" })}
		/>
	);
};

interface DiffViewProps {
	readonly data: AuditRecord[];
	readonly additionalDataTransform?: DataTransformFunction;
	readonly compareAll?: boolean;
	readonly showTypeColumn?: boolean;
	readonly showActivityColumn?: boolean;
	readonly autoExpand?: boolean;
	readonly actionColumn?: DiffViewActionColumn | null;
}

export const DiffView: FC<DiffViewProps> = ({
	data,
	additionalDataTransform,
	compareAll = false,
	showTypeColumn = true,
	showActivityColumn = true,
	autoExpand = true,
	actionColumn,
}) => {
	const intl = useIntl();
	const [expandedRows, setExpandedRows] = useState<string[]>([]);

	useLayoutEffect(() => {
		const defaultExpandedRows = data
			.filter((row, index) => (autoExpand && index === 0) || row.defaultExpanded === true)
			.map((row) => row.id);
		setExpandedRows(defaultExpandedRows);
	}, [autoExpand, data]);

	const columns = useMemo(() => {
		const arr: DiffViewColumn[] = [
			{
				id: "date",
				name: intl.formatMessage({ id: "Date", defaultMessage: "Date" }),
				getValue: (row: AuditRecord) => new Date(row.createDateUTC).toLocaleString(),
				width: "225px",
			},
			{
				id: "type",
				name: intl.formatMessage({ id: "Type", defaultMessage: "Type" }),
				getValue: (row: AuditRecord) => getAuditTypeDisplayName(row.auditType, intl),
				omit: !showTypeColumn,
			},
			{
				id: "activity",
				name: intl.formatMessage({ id: "Activity", defaultMessage: "Activity" }),
				getValue: (row: AuditRecord) => row.routingKey.split(".").at(-1) ?? "",
				omit: !showActivityColumn,
			},
			{
				id: "user",
				name: intl.formatMessage({ id: "User", defaultMessage: "User" }),
				getValue: (row: AuditRecord) => row.userId,
			},
		];
		if (actionColumn) {
			arr.push({
				id: "actions",
				name: actionColumn.name,
				getValue: (row: AuditRecord, rowIndex: number) =>
					actionColumn.cell(row, rowIndex, undefined, undefined, expandedRows),
				maxWidth: actionColumn.maxWidth,
				align: "right",
			});
		}
		return arr.filter((column) => column.omit !== true);
	}, [actionColumn, expandedRows, intl, showActivityColumn, showTypeColumn]);

	const onToggleExpand = useCallback((toggleState: boolean, row: AuditRecord): void => {
		setExpandedRows((prev) => {
			if (toggleState) {
				return [...prev, row.id];
			}
			return prev.filter((i) => i !== row.id);
		});
	}, []);

	const expandedRowContextValue = useMemo(
		() => ({ tableData: data, additionalDataTransform, compareAll }),
		[data, additionalDataTransform, compareAll],
	);

	if (!data || !Array.isArray(data) || !data.length) {
		return (
			<Grid container justifyContent="center" alignItems="center" sx={{ minHeight: 400 }}>
				<Typography variant="h2" color="text.secondary" fontWeight={400}>
					<FormattedMessage
						id="No audit history exists for this item"
						defaultMessage="No audit history exists for this item"
					/>
				</Typography>
			</Grid>
		);
	}

	return (
		<ExpandedRowContext.Provider value={expandedRowContextValue}>
			<TableContainer>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell sx={{ width: 56 }} />
							{columns.map((column) => (
								<TableCell
									key={column.id}
									align={column.align}
									sx={{
										width: column.width,
										maxWidth: column.maxWidth,
										fontWeight: 600,
									}}
								>
									{column.name}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{data.map((row, rowIndex) => {
							const isExpanded = expandedRows.includes(row.id);

							return (
								<ReactFragment key={row.id}>
									<TableRow
										hover
										sx={{
											"&:nth-of-type(odd)": {
												backgroundColor: "action.hover",
											},
										}}
									>
										<TableCell>
											<IconButton size="small" onClick={() => onToggleExpand(!isExpanded, row)}>
												{isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
											</IconButton>
										</TableCell>
										{columns.map((column) => (
											<TableCell key={`${column.id}-${row.id}`} align={column.align} sx={{ maxWidth: column.maxWidth }}>
												{column.getValue(row, rowIndex)}
											</TableCell>
										))}
									</TableRow>
									<TableRow>
										<TableCell colSpan={columns.length + 1} sx={{ paddingBottom: 0, paddingTop: 0 }}>
											<Collapse in={isExpanded} timeout="auto" unmountOnExit>
												<Box sx={{ paddingY: 2 }}>
													<ExpandedRowContent data={row} />
												</Box>
											</Collapse>
										</TableCell>
									</TableRow>
								</ReactFragment>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
		</ExpandedRowContext.Provider>
	);
};
